import React, { useState } from 'react';
import { ArrowDownTrayIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { apiUrl, authFetch, authService } from '../utils/api';
import {
  downloadQuizExcelTemplate,
  readQuizExcelFile,
  type QuizExcelQuestion
} from '../utils/quizExcel';

interface ImportQuizExcelModalProps {
  isOpen: boolean;
  onClose: () => void;
  targetUnitId: number;
  nextOrder: number;
  onImported: () => Promise<void> | void;
}

const ImportQuizExcelModal: React.FC<ImportQuizExcelModalProps> = ({
  isOpen,
  onClose,
  targetUnitId,
  nextOrder,
  onImported
}) => {
  const [quizTitle, setQuizTitle] = useState('');
  const [quizTimerMinutesStr, setQuizTimerMinutesStr] = useState('');
  const [fileName, setFileName] = useState('');
  const [questions, setQuestions] = useState<QuizExcelQuestion[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [isParsing, setIsParsing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const resetState = () => {
    setQuizTitle('');
    setQuizTimerMinutesStr('');
    setFileName('');
    setQuestions([]);
    setErrors([]);
    setIsParsing(false);
    setIsImporting(false);
    setSubmitError(null);
  };

  const handleClose = () => {
    if (isImporting) return;
    resetState();
    onClose();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    const allowed = /\.(xlsx|xls|csv)$/i.test(file.name);
    if (!allowed) {
      setFileName('');
      setQuestions([]);
      setErrors(['يرجى رفع ملف Excel (.xlsx أو .xls) أو CSV.']);
      return;
    }

    setIsParsing(true);
    setSubmitError(null);
    setFileName(file.name);
    try {
      const result = await readQuizExcelFile(file);
      setQuestions(result.questions);
      setErrors(result.errors);
      if (!quizTitle.trim()) {
        const baseName = file.name.replace(/\.(xlsx|xls|csv)$/i, '').replace(/[-_]+/g, ' ').trim();
        if (baseName) setQuizTitle(baseName);
      }
    } catch (err) {
      console.error('Error parsing quiz Excel:', err);
      setQuestions([]);
      setErrors(['تعذر قراءة الملف. حمّل القالب ثم حاول مرة أخرى.']);
    } finally {
      setIsParsing(false);
    }
  };

  const handleImport = async () => {
    if (!quizTitle.trim()) {
      setSubmitError('يرجى إدخال عنوان الاختبار');
      return;
    }
    if (questions.length === 0) {
      setSubmitError('ارفع ملف Excel يحتوي على سؤال واحد صالح على الأقل');
      return;
    }
    if (!targetUnitId) {
      setSubmitError('لم يتم تحديد الوحدة');
      return;
    }

    const timerTrim = quizTimerMinutesStr.trim();
    let quizTimerMinutes: number | null = null;
    if (timerTrim !== '') {
      const mins = parseFloat(timerTrim);
      if (Number.isNaN(mins) || mins < 0) {
        setSubmitError('أدخل مدة صحيحة بالدقائق، أو اترك الحقل فارغاً بدون مؤقت');
        return;
      }
      quizTimerMinutes = mins;
    }

    setIsImporting(true);
    setSubmitError(null);

    try {
      const response = await authFetch(apiUrl('Lesson/add'), {
        method: 'POST',
        headers: {
          accept: '*/*',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authService.getToken()}`
        },
        body: JSON.stringify({
          lessonName: quizTitle.trim(),
          titel: quizTitle.trim(),
          unitId: targetUnitId,
          active: true,
          order: nextOrder,
          type: 'quiz',
          quizTimer: quizTimerMinutes,
          questions
        })
      });
      const data = await response.json();
      if (!data.success) {
        throw new Error(data.message || 'فشل إنشاء الاختبار');
      }
      await onImported();
      resetState();
      onClose();
    } catch (err) {
      console.error('Error importing quiz from Excel:', err);
      setSubmitError(err instanceof Error ? err.message : 'فشل استيراد الاختبار. يرجى المحاولة مرة أخرى.');
    } finally {
      setIsImporting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold">استيراد اختبار من Excel</h2>
            <p className="text-sm text-gray-500 mt-1">
              حمّل القالب، املأ الأسئلة، ثم ارفع الملف. أبقِ صف العناوين كما هو.
            </p>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
            aria-label="إغلاق نافذة الاستيراد"
            disabled={isImporting}
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="space-y-4">
          <button
            type="button"
            onClick={downloadQuizExcelTemplate}
            className="inline-flex items-center gap-2 px-4 py-2 border border-primary text-primary rounded-lg hover:bg-primary/5"
          >
            <ArrowDownTrayIcon className="w-5 h-5" />
            تحميل قالب الاستيراد
          </button>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              عنوان الاختبار *
            </label>
            <input
              type="text"
              value={quizTitle}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuizTitle(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
              placeholder="أدخل عنوان الاختبار..."
              disabled={isImporting}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              الحد الزمني (بالدقائق)
            </label>
            <input
              type="number"
              min={0}
              step="any"
              value={quizTimerMinutesStr}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuizTimerMinutesStr(e.target.value)}
              className="w-full max-w-xs px-3 py-2 border rounded-lg"
              placeholder="اتركه فارغاً بدون مؤقت"
              disabled={isImporting}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              ملف Excel *
            </label>
            <input
              type="file"
              accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
              onChange={handleFileChange}
              disabled={isParsing || isImporting}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-white hover:file:bg-primary/90 disabled:opacity-50"
            />
            {fileName && (
              <p className="mt-1 text-xs text-gray-500">الملف المحدد: {fileName}</p>
            )}
            {isParsing && (
              <p className="mt-1 text-xs text-indigo-600">جارٍ قراءة ملف Excel...</p>
            )}
          </div>

          {errors.length > 0 && (
            <div className="bg-red-50 text-red-700 text-sm rounded-lg p-3 space-y-1">
              {errors.map(error => (
                <p key={error}>{error}</p>
              ))}
            </div>
          )}

          {questions.length > 0 && (
            <div className="border rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-3 py-2 text-sm font-medium text-gray-700">
                {questions.length} سؤال جاهز للاستيراد
              </div>
              <ul className="max-h-48 overflow-y-auto divide-y">
                {questions.map((question, index) => (
                  <li key={`${question.text}-${index}`} className="px-3 py-2 text-sm">
                    <span className="text-gray-500 ml-2">{index + 1}.</span>
                    <span className="font-medium">{question.type}</span>
                    <span className="text-gray-700"> — {question.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {submitError && (
            <div className="bg-red-50 text-red-700 text-sm rounded-lg p-3">
              {submitError}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-4 mt-6">
          <button
            onClick={handleClose}
            disabled={isImporting}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors disabled:opacity-50"
          >
            إلغاء
          </button>
          <button
            onClick={handleImport}
            disabled={isImporting || isParsing || questions.length === 0}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isImporting ? 'جارٍ الاستيراد...' : 'استيراد الاختبار'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImportQuizExcelModal;
